import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCalendar } from 'react-icons/fi';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--text-primary);
  }
`;

const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
`;

const SessionItem = styled(motion.button)`
  background: none;
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  
  &:hover {
    border-color: var(--primary-color);
    background-color: var(--background-secondary);
  }
`;

const SessionTitle = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const SessionDate = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
`;

const SessionStats = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--text-secondary);
  
  &.present {
    color: var(--success-color);
  }
  
  &.absent {
    color: var(--error-color);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

function SessionSelectionModal({ isOpen, onClose, sessions, onSessionSelected }) {
  if (!isOpen) return null;

  const handleSessionClick = (session) => {
    onSessionSelected(session);
  };

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>
              <FiCalendar size={20} />
              Select Session
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <FiX size={20} />
            </CloseButton>
          </ModalHeader>

          {sessions.length === 0 ? (
            <EmptyState>
              <FiCalendar size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>No attendance sessions found</p>
            </EmptyState>
          ) : (
            <SessionsList>
              {sessions.map((session, index) => (
                <SessionItem
                  key={session.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleSessionClick(session)}
                >
                  <SessionTitle>
                    {session.title || `${session.course_name} - ${session.course_code}`}
                  </SessionTitle>
                  <SessionDate>
                    {formatDateForDisplay(session.date, 'M/d/yyyy')}
                  </SessionDate>
                  <SessionStats>
                    <StatItem className="present">
                      <span>●</span>
                      {session.present_count || 0} Present
                    </StatItem>
                    <StatItem className="absent">
                      <span>●</span>
                      {session.absent_count || 0} Absent
                    </StatItem>
                    <StatItem>
                      <span>●</span>
                      {session.total_students || 0} Total
                    </StatItem>
                  </SessionStats>
                </SessionItem>
              ))}
            </SessionsList>
          )}
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>
  );
}

export default SessionSelectionModal;
